/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getLogger } from '../utils/SearchLogger';

class RenditionService {
	searchHostContext: HostContext;
	logger: Logger;

	constructor(searchHostContext: HostContext) {
		this.searchHostContext = searchHostContext;
		this.logger = getLogger();
	}

	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * [fetchBinary retrieves binary per the rendition id/url]
	 * @returns object - search datarequest response object
	 */
	fetchBinary(id: string) {
		return this._performRenditionDataRequest(id);
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */

	/**
	 * Retrieves binary from calling the rendition id/url.
	 * @param {assetID} asset id
	 * @returns {Object}
	 */
	_performRenditionDataRequest(id: string) {
		return new Promise((resolve, reject) => {
			const renditionRequest = new XMLHttpRequest();
			renditionRequest.open('GET', id, true);
			renditionRequest.setRequestHeader('Authorization', `Bearer ${this.searchHostContext.imsInfo.accessToken}`);
			renditionRequest.setRequestHeader('X-Api-Key', this.searchHostContext.hostInfo.apiKey);
			renditionRequest.responseType = 'arraybuffer';

			renditionRequest.onload = () => {
				let contentType = 'image/png';

				if (renditionRequest.getResponseHeader('Content-Type')) {
					contentType = renditionRequest.getResponseHeader('Content-Type') as string;
				}

				const arrayBuffer = renditionRequest.response;

				if (renditionRequest.status < 300 && arrayBuffer) {
					const byteArray = new Uint8Array(arrayBuffer);
					const blob = new Blob([byteArray], {
						type: contentType,
					});
					const url = URL.createObjectURL(blob);
					resolve(url);
				} else {
					const errorMessage = {
						renditionUrl: id,
						responseStatus: renditionRequest.status,
						responseStatusText: renditionRequest.statusText,
					};
					this.logger.error('Rendition Service', 'Rendition failure, blob creation failed', errorMessage);
					reject('ERROR');
				}
			};

			renditionRequest.onerror = () => {
				// rendition load error
				const errors = {
					renditionURL: id,
					status: renditionRequest.status,
					statusText: renditionRequest.statusText,
				};
				this.logger.error('Rendition Service', 'Rendition request failed', errors);
				reject(renditionRequest.statusText);
			};

			renditionRequest.send();
		});
	}
}

export default RenditionService;
